/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: providerTemplates.ts
 🗓️ Created: 2026-01-19
 🧩 Description:
    VoIP provider templates for Gateway Wizard. Contains pre-configured
    settings for popular SIP trunk providers.
    Provider data is split into tier1/tier2 files for maintainability.
────────────────────────────────────────────────────────────────*/

/**
 * Provider field definition - what the user needs to input
 */
export interface ProviderField {
    name: string;
    label: string;
    type: 'text' | 'password' | 'select';
    required: boolean;
    placeholder?: string;
    helpText?: string;
    options?: { value: string; label: string }[];
}

/**
 * Default gateway settings for a provider
 */
export interface ProviderDefaults {
    proxy: string;
    realm?: string;
    register: boolean;
    registerTransport: 'udp' | 'tcp' | 'tls';
    expireSeconds: number;
    retrySeconds: number;
    context: string;
    fromUser?: string;
    fromDomain?: string;
    callerIdInFrom?: boolean;
    // Advanced settings
    pingFreq?: number;
    pingMax?: number;
    pingMin?: number;
}

/**
 * Provider template definition
 */
export interface ProviderTemplate {
    id: string;
    name: string;
    description: string;
    website?: string;
    fields: ProviderField[];
    defaults: ProviderDefaults;
    notes?: string;
}

// ── Provider data (split for maintainability) ──────────────────
import { TIER1_PROVIDER_TEMPLATES } from './providerTemplates.tier1';
import { TIER2_PROVIDER_TEMPLATES } from './providerTemplates.tier2';

/**
 * VoIP Provider Templates — combined from tier1 + tier2
 */
export const PROVIDER_TEMPLATES: ProviderTemplate[] = [
    ...TIER1_PROVIDER_TEMPLATES,
    ...TIER2_PROVIDER_TEMPLATES,
];

// ── Utility functions ──────────────────────────────────────────

/**
 * Get a provider template by ID
 */
export function getProviderTemplate(id: string): ProviderTemplate | undefined {
    return PROVIDER_TEMPLATES.find(p => p.id === id);
}

/**
 * Apply user inputs to provider defaults to generate gateway config
 */
export function applyProviderTemplate(
    template: ProviderTemplate,
    userInputs: Record<string, string>
): Record<string, string | number | boolean> {
    const config: Record<string, string | number | boolean> = {
        ...template.defaults
    };

    // Apply user inputs
    for (const [key, value] of Object.entries(userInputs)) {
        if (value) {
            config[key] = value;
        }
    }

    // Handle dynamic proxy replacement (e.g., {region}.pstn.twilio.com)
    if (typeof config.proxy === 'string') {
        config.proxy = config.proxy.replace(/\{(\w+)\}/g, (_, key) => {
            return String(userInputs[key] || config[key] || '');
        });
    }

    // Handle sub-account for Voip.ms
    if (template.id === 'voipms' && userInputs.subAccount) {
        config.username = userInputs.subAccount;
    }

    return config;
}
